import { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Box,
  IconButton,
} from "@mui/material";
import { getActualSensorsDataByTables } from "../services/sensor.service";
import SensorCard from "../components/SensorCard";
import { EQUIPOS } from "../hooks/useDBTables";
import { RefreshCcw } from "lucide-react";
import { keyframes } from "@mui/system";
import { useRef } from "react";

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const SECONDS = 60;

export default function SensorView() {
  const [sensors, setSensors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchingData, setFetchingData] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const sensorsRef = useRef([]);

  const fetchSensorsData = async () => {
    try {
      setFetchingData(true);
      const response = await getActualSensorsDataByTables(EQUIPOS);
      setSensors(response);
      sensorsRef.current = response;
      setLastUpdated(new Date());
    } catch (err) {
      if (sensorsRef.current.length === 0) {
        setError(err);
      } 
    } finally {
      setFetchingData(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSensorsData();

    // Set interval to fetch data every 1 minutes (60000 ms)
    const intervalId = setInterval(() => {
      fetchSensorsData();
    }, SECONDS * 1000);

    // Clean up the interval when the component is unmounted
    return () => clearInterval(intervalId);
  }, []);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="80vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ mt: 10 }}>
        <Alert severity="error">Error: {error.message}</Alert>
      </Container>
    );
  }

  const formatDate = (dateInput) => {
    const date =
      typeof dateInput === "string" ? new Date(dateInput) : dateInput;
    const today = new Date();

    const isToday =
      date.getUTCDate() === today.getUTCDate() &&
      date.getUTCMonth() === today.getUTCMonth() &&
      date.getUTCFullYear() === today.getUTCFullYear();

    const hours = date.getUTCHours();
    const minutes = date.getUTCMinutes().toString().padStart(2, "0");

    const timeString = `${hours}:${minutes}hs`;

    return isToday
      ? `Hoy ${timeString}`
      : `${date.getUTCDate()}/${
          date.getUTCMonth() + 1
        }/${date.getUTCFullYear()} ${timeString}`;
  };

  return (
    <Container maxWidth="md" sx={{ mt: 10 }}>
      <head>
        <title>Temperaturas de Sensores</title>
      </head>
      <Typography variant="h4" align="center" gutterBottom>
        Temperaturas de Sensores
      </Typography>
      {lastUpdated && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginBottom: "16px",
          }}
        >
          <Typography
            variant="subtitle2"
            align="center"
            alignSelf={"center"}
            color="textSecondary"
          >
            Última actualización:{" "}
            {` ${lastUpdated
              .getHours()
              .toString()
              .padStart(2, "0")}:${lastUpdated
              .getMinutes()
              .toString()
              .padStart(2, "0")}hs`}
          </Typography>
          <IconButton
            onClick={fetchSensorsData}
            disabled={loading || fetchingData}
            sx={{
              color: "black",
              borderRadius: "8px",
              transition: "all 0.2s ease-in-out",
              animation: fetchingData ? `${spin} 1s linear infinite` : "none",

              "&:hover": {
                color: "black",
                transform: fetchingData ? "none" : "rotate(90deg)",
              },
            }}
          >
            <RefreshCcw size={18} />
          </IconButton>
        </div>
      )}

      <Grid
        container
        spacing={{ xs: 1.5, sm: 2, md: 3 }}
        sx={{ display: "flex", justifyContent: "center" }}
      >
        {sensors.map((sensor) => (
          <Grid item xs={12} sm={6} md={4} key={sensor.id}>
            <SensorCard sensor={sensor} formatDate={formatDate} />
          </Grid>
        ))}
      </Grid>
    </Container>
  );
}
